import { useCallback, useEffect, useState } from "react";
import { useMediaQuery } from "usehooks-ts";

import { DESKTOP_QUERY } from "@/components/layout/layout.constants";

/**
 * Sidebar open/close state, synced to the viewport:
 * - opens by default at desktop width, closed below it (post-hydration) —
 *   below desktop the sidebar renders as an overlay drawer, so it should
 *   never cover content on first load.
 * - auto-closes when the viewport shrinks below desktop width.
 */
export function useResponsiveSidebar() {
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
    if (typeof window !== "undefined") {
      setIsSidebarOpen(window.matchMedia(DESKTOP_QUERY).matches);
    }
  }, []);

  useEffect(() => {
    if (isHydrated && !isDesktop) setIsSidebarOpen(false);
  }, [isDesktop, isHydrated]);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((open) => !open);
  }, []);

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  const openSidebar = useCallback(() => {
    setIsSidebarOpen(true);
  }, []);

  return { isSidebarOpen, toggleSidebar, closeSidebar, openSidebar };
}
